<template>
  <div class="social-account-callback-view d-flex fill-height">
    <Spinner class="ma-auto"/>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { RouteLocationRaw } from 'vue-router';
import API from '@/api/wrapper';
import Spinner from '@/components/UIElements/Spinner.vue';
import { ToastrDescriptor } from '@/types';
import { APISocialAccount, APIUrlRedirection } from '@/typesAPI';
import { forgetSocialAccountReturnState, rememberedSocialAccountReturnState } from '@/utils/socialAccountReturn';

@Options({
  components: {
    Spinner,
  },
})
export default class SocialAccountCallbackView extends Vue {

  mounted(): void {
    const code = this.$route.query.code

    if (typeof code === 'string') {
      this.link(code)
      return
    }

    // Installing the GitHub app gives a code only on a new installation: after an update of the installation,
    // authorising the app gives one.
    if (this.socialProvider === 'github' && typeof this.$route.query.installation_id === 'string') {
      this.authoriseGithubApp()
      return
    }

    // The person refused, or the provider sent nothing to link.
    if (this.$route.query.error) {
      this.showFailure()
    }
    this.finish()
  }

  get socialProvider(): string {
    return this.$route.params.socialProvider.toString()
  }

  link(code: string): void {
    API.social.callback(this.$store.getters['user/getUser'].id, this.socialProvider, code, this.redirectUrl)
    .then((socialAccount:APISocialAccount) => {
      this.$store.dispatch('user/setSocialAccount', {provider: this.socialProvider, socialAccount: socialAccount})
    })
    .catch((err:any) => {
      if (!err?.toastr?.id) {
        this.showFailure()
      }
    })
    .finally(() => {
      this.finish()
    })
  }

  authoriseGithubApp(): void {
    const callbackUrl = new URL(window.location.pathname, window.location.origin)
    if (this.state) {
      callbackUrl.searchParams.set('state', this.state)
    }

    API.social.connect(this.$store.getters['user/getUser'].id, this.socialProvider, callbackUrl.toString(), true)
    .then((urlRedirection:APIUrlRedirection) => {
      window.location.href = urlRedirection.attributes.url
    })
    .catch(() => {
      this.showFailure()
      this.finish()
    })
  }

  showFailure(): void {
    const descriptor:ToastrDescriptor = {
      title: '',
      text: 'Your account could not be connected. Please try again.',
      severity: this.$enums.Severity.DANGER,
      id: `social-account-callback-${this.socialProvider}`,
    }
    this.$store.dispatch('toastrs/addDescriptor', descriptor)
  }

  finish(): void {
    const returnRoute = this.returnRoute
    forgetSocialAccountReturnState()
    this.$router.replace(returnRoute)
  }

  // The URL the provider sent the person back to, which it requires again to exchange the code.
  get redirectUrl():string {
    const urlParams = new URLSearchParams(window.location.search)
    urlParams.delete('code')

    return `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`
  }

  // Where the person started from: sent back by the provider, or kept by this tab when GitHub drops it.
  get state(): string | undefined {
    return new URLSearchParams(window.location.search).get('state') || rememberedSocialAccountReturnState()
  }

  get returnRoute():RouteLocationRaw {
    const origin:{[key:string]: string} = {}
    this.state?.split('|').forEach((part:string) => {
      const [key, value] = part.split('=')
      origin[key] = value
    })
    const {projectId, blockId, tabId, boxId} = origin

    if (projectId && blockId && tabId && boxId) {
      return {name: 'flowBlockAndBox', params: {projectId, blockId, tabId, boxId}}
    }
    if (projectId && blockId && tabId) {
      return {name: 'flowBlock', params: {projectId, blockId, tabId}}
    }
    if (projectId) {
      return {name: 'flow', params: {projectId}}
    }

    return {name: 'home'}
  }
}
</script>
